import React, { useState, useEffect, useRef } from "react";
import { FaClock, FaCopy, FaQuestionCircle, FaCog, FaTrash, FaTimes } from "react-icons/fa";
import { MdFeedback } from "react-icons/md";
import "./SuppliersDetailPage.css";

// Date formatting utility functions
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const parseDDMMYYYYToDate = (dateString) => {
  if (!dateString) return "";

  try {
    const [day, month, year] = dateString.split('-');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error parsing date:", error);
    return dateString;
  }
};

// Define getCurrentDate function
const getCurrentDate = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `${day}-${month}-${year}`;
};

// Get current date in YYYY-MM-DD format for input fields
const getCurrentDateForInput = () => {
  return new Date().toISOString().split('T')[0];
};

const CreateBillPopup = ({ supplierId, supplierData, billData, onClose, onBillCreated, onBillUpdated }) => {
  const API_BASE_URL = "https://backend.hira-tech.co.uk";
  const createPopupRef = useRef(null);
  const fileInputRef = useRef(null);
  const isEditMode = !!billData;
  const [categories, setCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [supplierEmail, setSupplierEmail] = useState(supplierData?.email || "");
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(
    billData && billData.items && billData.items.length > 0
      ? billData.items.map((item) => ({
        id: Date.now() + Math.random(),
        rowNumber: item.rowNumber || (billData.items.indexOf(item) + 1).toString(),
        category: item.category || billData.terms || "General",
        description: item.description || billData.memo || "Initial Item",
        totalAmount: item.totalAmount ? parseFloat(item.totalAmount).toString() : "", // Convert to string, empty if undefined
      }))
      : [
        {
          id: Date.now(),
          rowNumber: "1",
          category: billData?.terms || "General",
          description: billData?.memo || "Initial Item",
          totalAmount: "", // Empty string
        },
      ]
  );
  const [createBillFile, setCreateBillFile] = useState(
    billData && billData.file_name && billData.file_name !== "Manual Entry"
      ? { name: billData.file_name }
      : null
  );
  const [totalAmount, setTotalAmount] = useState(0);
  const [newBillData, setNewBillData] = useState(
    billData
      ? {
        billNumber: billData.bill_number || billData.no || "",
        supplier: supplierId || billData.supplier || "",
        address: billData.address || supplierData?.email || "",
        terms: billData.terms || "General",
        billDate: formatDateToDDMMYYYY(billData.date) || getCurrentDate(), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(billData.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
        memo: billData.memo || "",
        status: billData.status || "Pending",
      }
      : {
        billNumber: "",
        supplier: supplierId || "",
        address: supplierData?.email || "",
        terms: "General",
        billDate: getCurrentDate(), // DD-MM-YYYY format
        dueDate: getCurrentDate(), // DD-MM-YYYY format
        memo: "",
        status: "Pending",
      }
  );

  // Fetch supplier details
  useEffect(() => {
    if (supplierId && !supplierData?.email) {
      fetch(`${API_BASE_URL}/api/suppliers/${supplierId}/`, {
        headers: { Accept: "application/json" },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Supplier fetch error! Status: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          setSupplierEmail(data.email || "");
          setNewBillData((prev) => ({
            ...prev,
            address: data.email || "",
          }));
          setError(null);
        })
        .catch((error) => {
          console.error(`Failed to fetch supplier email: ${error.message}`);
          setSupplierEmail("");
          setNewBillData((prev) => ({ ...prev, address: "" }));
          setError("Failed to fetch supplier email. Please try again.");
        });
    } else if (supplierData?.email) {
      setSupplierEmail(supplierData.email);
      setNewBillData((prev) => ({
        ...prev,
        address: supplierData.email,
      }));
      setError(null);
    }
  }, [supplierId, supplierData]);

  // Fetch categories from backend
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/categories/`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Categories fetch error! Status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setCategories(data);
        const map = data.reduce((acc, cat) => {
          acc[cat.name] = cat.display_name || cat.name;
          return acc;
        }, {});
        setCategoryMap(map);
        if (newBillData.terms && !data.some(cat => cat.name === newBillData.terms)) {
          setNewBillData(prev => ({ ...prev, terms: data[0]?.name || "General" }));
          setTableData(prev => prev.map(row => ({ ...row, category: data[0]?.name || "General" })));
        }
      })
      .catch((error) => {
        console.error(`Failed to fetch categories: ${error.message}`);
        setCategoryMap({ General: "General" });
        setCategories([{ id: 1, name: "General", display_name: "General" }]);
      });
  }, [newBillData.terms]);

  // Calculate total amount in GBP
  useEffect(() => {
    const calculatedTotal = tableData.reduce((sum, row) => {
      const amount = row.totalAmount === "" ? 0 : parseFloat(row.totalAmount) || 0;
      return sum + amount;
    }, 0);
    setTotalAmount(calculatedTotal);
  }, [tableData]);

  // Handle outside click to close popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (createPopupRef.current && !createPopupRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleBillSubmit = async (e, saveAndNew = false) => {
    e.preventDefault();
    if (!createBillFile && !isEditMode) {
      alert("A PDF file is required to create a bill.");
      return;
    }

    if (
      tableData.some(
        (item) =>
          item.totalAmount === "" ||
          isNaN(parseFloat(item.totalAmount)) ||
          parseFloat(item.totalAmount) <= 0
      )
    ) {
      alert("All item amounts must be positive numbers greater than 0.");
      return;
    }

    const total = totalAmount;
    console.log("Input totalAmount:", total.toFixed(2));
    const formData = new FormData();
    if (createBillFile && (!isEditMode || createBillFile.name !== billData?.file_name)) {
      formData.append("file", createBillFile);
    }
    formData.append("bill_number", newBillData.billNumber || `BILL-${Date.now()}`);
    formData.append("supplier", newBillData.supplier ? parseInt(newBillData.supplier) : "");
    formData.append("amount", total.toFixed(2));
    formData.append("address", newBillData.address || "");
    const termsValue = categories.find(cat => cat.name === newBillData.terms)?.name || categories[0]?.name || "General";
    formData.append("terms", termsValue);

    // Convert DD-MM-YYYY to YYYY-MM-DD for API
    const apiBillDate = newBillData.billDate ? parseDDMMYYYYToDate(newBillData.billDate) : getCurrentDateForInput();
    const apiDueDate = newBillData.dueDate ? parseDDMMYYYYToDate(newBillData.dueDate) : getCurrentDateForInput();

    if (apiBillDate && /^\d{4}-\d{2}-\d{2}$/.test(apiBillDate)) {
      formData.append("date", apiBillDate);
    }
    if (apiDueDate && /^\d{4}-\d{2}-\d{2}$/.test(apiDueDate)) {
      formData.append("due_date", apiDueDate);
    }
    formData.append("memo", newBillData.memo || "");
    formData.append("status", newBillData.status || "Pending");
    if (createBillFile) formData.append("file_name", createBillFile.name);
    formData.append(
      "items",
      JSON.stringify(
        tableData.map((item) => ({
          rowNumber: item.rowNumber,
          category: categories.find(cat => cat.name === item.category)?.name || termsValue,
          description: item.description,
          totalAmount: parseFloat(item.totalAmount).toFixed(2),
        }))
      )
    );

    console.log("FormData being sent:", Object.fromEntries(formData));

    try {
      const url = isEditMode ? `${API_BASE_URL}/api/bills/${billData.id}/` : `${API_BASE_URL}/api/bills/`;
      const method = isEditMode ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: {
          // Add authentication headers if required
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to ${isEditMode ? "update" : "create"} bill: ${JSON.stringify(errorData)}`);
      }

      const bill = await response.json();
      console.log("API response:", bill);
      const updatedBill = {
        id: bill.id,
        type: "Bill",
        no: bill.bill_number,
        payee: supplierData?.name || "Unknown",
        category: bill.category || (bill.terms ? (categoryMap[bill.terms] || bill.terms) : "Purchases"),
        terms: bill.terms || "General",
        totalBeforeTax: parseFloat(bill.amount || bill.total || 0).toFixed(2),
        salesTax: "0.00",
        total: parseFloat(bill.amount || bill.total || 0).toFixed(2),
        status: bill.status,
        address: bill.address,
        date: formatDateToDDMMYYYY(bill.date), // Format date to DD-MM-YYYY
        due_date: formatDateToDDMMYYYY(bill.due_date), // Format date to DD-MM-YYYY
        memo: bill.memo || "",
        supplier: bill.supplier,
        bill_number: bill.bill_number,
        file_name: bill.bill_pdf ? bill.bill_pdf.split("/").pop() : "Manual Entry",
        file_path: bill.bill_pdf ? `${API_BASE_URL}${bill.bill_pdf}` : "",
        items: bill.items
          ? bill.items.map((item) => ({
            id: Date.now() + Math.random(),
            rowNumber: item.rowNumber || "1",
            category: item.category || bill.terms || "General",
            description: item.description || bill.memo || "Initial Item",
            totalAmount: parseFloat(item.totalAmount || 0).toFixed(2),
          }))
          : tableData,
      };

      if (isEditMode) {
        onBillUpdated(updatedBill);
      } else {
        onBillCreated(updatedBill);
      }

      if (!saveAndNew) {
        onClose();
      } else {
        setNewBillData({
          billNumber: "",
          supplier: supplierId || "",
          address: supplierEmail || "",
          terms: categories[0]?.name || "General",
          billDate: getCurrentDate(), // DD-MM-YYYY format
          dueDate: getCurrentDate(), // DD-MM-YYYY format
          memo: "",
          status: "Pending",
        });
        setTableData([
          { id: Date.now(), rowNumber: "1", category: categories[0]?.name || "General", description: "Initial Item", totalAmount: "" },
        ]);
        setCreateBillFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      alert(`Bill ${isEditMode ? "updated" : "created"} successfully!`);
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} bill:`, error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleMakePayment = async () => {
    if (!isEditMode || !billData?.id) {
      alert("Cannot make payment: Invalid bill data.");
      return;
    }

    if (newBillData.status === "Paid") {
      alert("This bill has already been paid.");
      return;
    }

    if (
      tableData.some(
        (item) =>
          item.totalAmount === "" ||
          isNaN(parseFloat(item.totalAmount)) ||
          parseFloat(item.totalAmount) <= 0
      )
    ) {
      alert("All item amounts must be positive numbers greater than 0.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/bills/${billData.id}/pay/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.message || "Failed to process payment. Please try again.";
        throw new Error(errorMessage);
      }

      const updatedBill = await response.json();
      console.log("Payment API response:", updatedBill);

      const billAmount = parseFloat(updatedBill.amount || updatedBill.total || 0).toFixed(2);
      const categoryValue = updatedBill.terms ? (categoryMap[updatedBill.terms] || updatedBill.terms) : "Purchases";

      const mappedBill = {
        id: updatedBill.id || billData.id,
        type: "Bill",
        no: updatedBill.bill_number || billData.bill_number || "",
        payee: supplierData?.name || "Unknown",
        category: categoryValue,
        terms: updatedBill.terms || "General",
        totalBeforeTax: billAmount ? (parseFloat(billAmount) / 1.17).toFixed(2) : "0.00",
        salesTax: billAmount ? (parseFloat(billAmount) * 0.17 / 1.17).toFixed(2) : "0.00",
        total: billAmount || "0.00",
        status: updatedBill.status || "Paid",
        address: updatedBill.address || billData.address || "",
        date: formatDateToDDMMYYYY(updatedBill.paid_date) || formatDateToDDMMYYYY(updatedBill.date) || getCurrentDate(), // Format date to DD-MM-YYYY
        due_date: formatDateToDDMMYYYY(updatedBill.due_date) || formatDateToDDMMYYYY(billData.due_date) || "", // Format date to DD-MM-YYYY
        memo: updatedBill.memo || billData.memo || "",
        supplier: updatedBill.supplier || supplierId || "",
        bill_number: updatedBill.bill_number || billData.bill_number || "",
        file_name: updatedBill.bill_pdf
          ? updatedBill.bill_pdf.split("/").pop()
          : billData.file_name || "Manual Entry",
        file_path: updatedBill.bill_pdf
          ? `${API_BASE_URL}${updatedBill.bill_pdf}`
          : billData.file_path || "",
        items: updatedBill.items
          ? updatedBill.items.map((item) => ({
            id: Date.now() + Math.random(),
            rowNumber: item.rowNumber || "1",
            category: item.category || updatedBill.terms || "General",
            description: item.description || updatedBill.memo || "Initial Item",
            totalAmount: parseFloat(item.totalAmount || 0).toFixed(2),
          }))
          : tableData,
      };

      onBillUpdated(mappedBill);

      setNewBillData({
        ...newBillData,
        status: "Paid",
        billDate: formatDateToDDMMYYYY(updatedBill.paid_date) || formatDateToDDMMYYYY(updatedBill.date) || newBillData.billDate, // Format date to DD-MM-YYYY
        billNumber: updatedBill.bill_number || newBillData.billNumber,
        memo: updatedBill.memo || newBillData.memo || "",
        address: updatedBill.address || newBillData.address,
        terms: updatedBill.terms || newBillData.terms,
      });

      setTableData(
        updatedBill.items
          ? updatedBill.items.map((item) => ({
            id: Date.now() + Math.random(),
            rowNumber: item.rowNumber || "1",
            category: item.category || updatedBill.terms || "General",
            description: item.description || updatedBill.memo || "Initial Item",
            totalAmount: parseFloat(item.totalAmount || 0).toFixed(2),
          }))
          : tableData
      );

      alert("Payment processed successfully!");
    } catch (error) {
      console.error("Error processing payment:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleAddRow = () => {
    setTableData([
      ...tableData,
      {
        id: Date.now(),
        rowNumber: (tableData.length + 1).toString(),
        category: newBillData.terms || categories[0]?.name || "General",
        description: newBillData.memo || "New Item",
        totalAmount: "", // Empty string
      },
    ]);
  };

  const handleDeleteRow = (rowId) => {
    setTableData(
      tableData
        .filter((row) => row.id !== rowId)
        .map((row, index) => ({ ...row, rowNumber: (index + 1).toString() }))
    );
  };

  const handleCopyRow = (rowId) => {
    const rowToCopy = tableData.find((row) => row.id === rowId);
    if (rowToCopy) {
      setTableData([
        ...tableData,
        { ...rowToCopy, id: Date.now(), rowNumber: (tableData.length + 1).toString() },
      ]);
    }
  };

  const handleUpdateRow = (rowId, field, value) => {
    setTableData(
      tableData.map((row) =>
        row.id === rowId
          ? {
            ...row,
            [field]:
              field === "totalAmount"
                ? value === ""
                  ? ""
                  : !isNaN(value) && value.match(/^\d*\.?\d{0,2}$/)
                    ? value
                    : row.totalAmount
                : value,
          }
          : row
      )
    );
    if (field === "category" && tableData.find((row) => row.id === rowId).rowNumber === "1") {
      setNewBillData({ ...newBillData, terms: value });
    }
  };

  const handleClearAllLines = () => {
    setTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: newBillData.terms || categories[0]?.name || "General",
        description: newBillData.memo || "Initial Item",
        totalAmount: "", // Empty string
      },
    ]);
  };

  const handleClear = () => {
    setNewBillData({
      billNumber: "",
      supplier: supplierId || "",
      address: supplierEmail || "",
      terms: categories[0]?.name || "General",
      billDate: getCurrentDate(), // DD-MM-YYYY format
      dueDate: getCurrentDate(), // DD-MM-YYYY format
      memo: "",
      status: "Pending",
    });
    setTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: categories[0]?.name || "General",
        description: "Initial Item",
        totalAmount: "", // Empty string
      },
    ]);
    setCreateBillFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRecurring = () => {
    alert("Recurring bill creation is not implemented yet.");
  };

  // Handle date input changes - convert from DD-MM-YYYY to text input
  const handleDateChange = (field, value) => {
    setNewBillData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="upload-popup-overlay">
      <div className="create-bill-popup" ref={createPopupRef}>
        <div className="bill-form-header">
          <div className="header-title">
            <FaClock className="header-icon" />
            <span>{isEditMode ? "Edit Bill" : "Bill"}</span>
          </div>
          <div className="header-actions">
            <button type="button" className="icon-btn" onClick={() => alert("Copy bill not implemented")}>
              <FaCopy />
              <span>Copy</span>
            </button>
            <button type="button" className="text-icon-btn">
              <MdFeedback />
              <span>Give Feedback</span>
            </button>
            <button type="button" className="icon-btn">
              <FaQuestionCircle />
            </button>
            <button
              className="close-button"
              type="button"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <h2 className="form-main-title">{isEditMode ? "Edit Bill" : "Bill"}</h2>
        {error && <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

        <form onSubmit={(e) => handleBillSubmit(e, false)} className="bill-form-container">
          <div className="form-section">
            <div className="form-row">
              <div className="form-group wide-group">
                <label>Supplier</label>
                <select
                  value={newBillData.supplier}
                  onChange={(e) => setNewBillData({ ...newBillData, supplier: e.target.value })}
                  disabled
                  style={{ width: "300px" }}
                >
                  <option value={supplierId}>{supplierData?.name || "Loading..."}</option>
                </select>
              </div>
              <div className="form-group amount-display">
                <label>Balance Due</label>
                <p style={{ fontWeight: "bold", color: "#333", fontSize: "25px" }}>
                  £{totalAmount.toFixed(2)}
                </p>
                {isEditMode && newBillData.status !== "Paid" && (
                  <button
                    type="button"
                    className="make-payment"
                    onClick={handleMakePayment}
                    style={{
                      marginTop: "0.5rem",
                      padding: "0.5rem 1rem",
                      backgroundColor: "#004aad",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Make Payment
                  </button>
                )}
              </div>
            </div>

            <div className="form-row five-items-row">
              <div className="form-group">
                <label>Mailing address</label>
                <input
                  type="text"
                  placeholder="Enter mailing address"
                  value={newBillData.address}
                  onChange={(e) => setNewBillData({ ...newBillData, address: e.target.value })}
                  style={{ width: "200px" }}
                />
              </div>
              <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-80px" }}>
                <label>Terms</label>
                <select
                  value={newBillData.terms}
                  onChange={(e) => {
                    const newTerms = e.target.value;
                    setNewBillData({ ...newBillData, terms: newTerms });
                    setTableData(tableData.map(row => ({ ...row, category: newTerms })));
                  }}
                >
                  <option value="">Select terms</option>
                  {categories.length > 0 ? (
                    categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.display_name || cat.name}
                      </option>
                    ))
                  ) : (
                    <option value="General">General</option>
                  )}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-160px" }}>
                <label>Bill Date</label>
                <input
                  type="text"
                  placeholder="DD-MM-YYYY"
                  value={newBillData.billDate}
                  onChange={(e) => handleDateChange("billDate", e.target.value)}
                  style={{ width: "200px" }}
                />
              </div>
              <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-240px" }}>
                <label>Due Date</label>
                <input
                  type="text"
                  placeholder="DD-MM-YYYY"
                  value={newBillData.dueDate}
                  onChange={(e) => handleDateChange("dueDate", e.target.value)}
                  style={{ width: "200px" }}
                />
              </div>
              <div className="form-group" style={{ width: "200px", marginLeft: "-320px" }}>
                <label>Bill number</label>
                <input
                  type="text"
                  value={newBillData.billNumber}
                  onChange={(e) => setNewBillData({ ...newBillData, billNumber: e.target.value })}
                />
              </div>
            </div>

            <div className="table-section">
              <div className="table-header">
                <span>Items</span>
                <button
                  type="button"
                  className="settings-btn"
                  onClick={() => alert("Settings not implemented yet")}
                >
                  <FaCog />
                </button>
              </div>
              <div className="table-container">
                <table className="item-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row) => (
                      <tr key={row.id}>
                        <td>{row.rowNumber}</td>
                        <td>
                          <select
                            value={row.category}
                            onChange={(e) => handleUpdateRow(row.id, "category", e.target.value)}
                          >
                            <option value="">Select category</option>
                            {categories.length > 0 ? (
                              categories.map((cat) => (
                                <option key={cat.id} value={cat.name}>
                                  {cat.display_name || cat.name}
                                </option>
                              ))
                            ) : (
                              <option value="General">General</option>
                            )}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={row.description}
                            onChange={(e) => handleUpdateRow(row.id, "description", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={row.totalAmount}
                            onChange={(e) => handleUpdateRow(row.id, "totalAmount", e.target.value)}
                            placeholder="Enter amount"
                            pattern="\d*\.?\d{0,2}"
                            title="Enter a number with up to two decimal places (e.g., 90 or 90.54)"
                          />
                        </td>
                        <td className="action-buttons">
                          <button
                            type="button"
                            className="icon-button copy"
                            onClick={() => handleCopyRow(row.id)}
                            title="Copy"
                          >
                            <FaCopy />
                          </button>
                          <button
                            type="button"
                            className="icon-button delete"
                            onClick={() => handleDeleteRow(row.id)}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan="5"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: "1rem", marginTop: "1rem", alignItems: "flex-start", marginTop: "-10px" }}>
                <button
                  type="button"
                  className="add-button"
                  onClick={handleAddRow}
                  title="Add Row"
                >
                  Add Lines
                </button>
                <button
                  type="button"
                  className="add-secondary-button"
                  onClick={handleClearAllLines}
                  title="Clear Lines"
                >
                  Clear all Lines
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "right", marginLeft: "1280px", marginTop: "-50px" }}>
                <label style={{ fontSize: "12px", fontWeight: "bold" }}>Total Amount</label>
                <p style={{ fontWeight: "bold", color: "#333", fontSize: "12px" }}>
                  : £{totalAmount.toFixed(2)}
                </p>
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", alignItems: "flex-start" }}>
                <div className="form-group" style={{ flex: 1, marginBottom: "-50px", marginTop: "30px" }}>
                  <label>Memo/Description</label>
                  <textarea
                    value={newBillData.memo}
                    onChange={(e) => setNewBillData({ ...newBillData, memo: e.target.value })}
                    placeholder={newBillData.memo ? newBillData.memo : "Enter memo or description"}
                    style={{
                      width: "100%",
                      height: "100px",
                      padding: "0.7rem",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      resize: "vertical",
                    }}
                  />
                </div>
                <div className="form-group" style={{ marginTop: "1rem" }}>
                  <label>Bill PDF {isEditMode ? "(Optional)" : "(Required)"}</label>
                  <div
                    className="upload-area"
                    style={{
                      padding: "0.5rem",
                      height: "100px",
                      marginTop: "15px",
                      width: "100%",
                      textAlign: "center",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                    onClick={() => fileInputRef.current.click()}
                  >
                    <p style={{ margin: "0", fontSize: "0.9rem" }}>
                      Click to select PDF file
                    </p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file && file.name.endsWith(".pdf")) {
                          if (file.size > 20 * 1024 * 1024) {
                            alert("File size exceeds 20MB limit.");
                            return;
                          }
                          setCreateBillFile(file);
                        } else {
                          alert("Please select a PDF file.");
                        }
                      }}
                      style={{ display: "none" }}
                    />
                    {createBillFile && (
                      <p
                        style={{
                          margin: "0.3rem 0",
                          color: "#333",
                          fontSize: "0.8rem",
                        }}
                      >
                        Selected: {createBillFile.name}
                      </p>
                    )}
                    {!createBillFile && (
                      <p
                        style={{
                          margin: "0.3rem 0",
                          color: "#666",
                          fontSize: "0.8rem",
                        }}
                      >
                        {isEditMode ? "No new file selected" : "PDF file is required"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "center", marginTop: "1rem" }}>
                <button
                  type="button"
                  style={{
                    padding: "0.7rem 1.5rem",
                    backgroundColor: "transparent",
                    color: "#004aad",
                    fontWeight: "bold",
                    marginTop: "-10px",
                    marginBottom: "-20px",
                  }}
                  onClick={() => alert("Privacy settings applied!")}
                >
                  Privacy
                </button>
              </div>
            </div>
          </div>

          <div
            className="form-action-buttons"
            style={{
              padding: "1rem",
              backgroundColor: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #ddd",
              marginTop: "auto",
            }}
          >
            <div>
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                style={{ marginRight: "1rem" }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="clear-btn"
                onClick={handleClear}
                style={{ marginRight: "0" }}
              >
                Clear
              </button>
            </div>
            <div>
              <button
                type="button"
                className="print-btn"
                onClick={handlePrint}
                style={{ marginRight: "1rem", borderRadius: "3px" }}
              >
                Print
              </button>
              <button
                type="button"
                className="recurring-btn"
                onClick={handleRecurring}
                style={{ marginRight: isEditMode && newBillData.status !== "Paid" ? "1rem" : "0" }}
              >
                Recurring
              </button>
            </div>
            <div>
              <button
                type="submit"
                className="submit-btn"
                style={{ marginRight: "1rem" }}
                disabled={!createBillFile && !isEditMode}
              >
                Save
              </button>
              <button
                type="button"
                className="save-new-btn"
                onClick={(e) => handleBillSubmit(e, true)}
                style={{ marginRight: "0" }}
                disabled={!createBillFile && !isEditMode}
              >
                Save and New
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBillPopup;